import { getDatabase, ref, get, update } from 'firebase/database';
import { app } from '../firebase';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { generateSlug } from '../Helpers/slugUtils';
import { recordActivity } from './activityApi';
import { LibraryConfig, usesBaseTypes } from '../library/LibraryConfig';
import { maintenanceLibrary } from '../library/maintenanceLibrary';
import { lifetimeLibrary } from '../library/lifetimeLibrary';
import { inspectionLibrary } from '../library/inspectionLibrary';

/**
 * API for exporting instance configuration to a JSON file and importing
 * such a file into another (typically freshly created) instance.
 *
 * Export: superadmin page /superadmin/config-export
 * Import: Konfigurationsstatus page (/setup), admin only
 *
 * The file only carries reusable configuration catalogs — never buildings,
 * users, or instance branding. Import REPLACES each selected section:
 * entries in the file are written and existing entries not in the file are
 * removed, via one multi-path update per section (several paths only allow
 * per-child writes, so whole-node set() is not an option).
 */

export const EXPORT_FORMAT = 'mf-config-export';
export const EXPORT_VERSION = 1;

export interface ConfigSectionDefinition {
    key: string;
    path: string;
    label: string;
    description: string;
}

export const CONFIG_SECTIONS: ConfigSectionDefinition[] = [
    {
        key: 'buildComponentTypes',
        path: 'config/buildComponentTypes',
        label: 'Komponenttyper',
        description: 'Komponenttyper med livslängder och egenskaper',
    },
    {
        key: 'maintenanceTemplates',
        path: 'config/maintenanceTemplates',
        label: 'Underhållsmallar',
        description: 'Underhållsmallar kopplade till komponenttyper',
    },
    {
        key: 'maintenanceLibraryTemplates',
        path: maintenanceLibrary.path,
        label: 'Underhållsbibliotek',
        description: 'Centrala mallar för underhållsaktiviteter (superadmin)',
    },
    {
        key: 'lifetimeLibraryTemplates',
        path: lifetimeLibrary.path,
        label: 'Livslängdsbibliotek',
        description: 'Centrala mallar för livslängd och ersättningskostnad (superadmin)',
    },
    {
        key: 'inspectionLibraryTemplates',
        path: inspectionLibrary.path,
        label: 'Kontrollbibliotek',
        description: 'Centrala mallar för kontroller (superadmin)',
    },
    {
        key: 'documentCategories',
        path: 'config/documentCategories',
        label: 'Dokumentkategorier',
        description: 'Kategorier för dokumenthantering',
    },
    {
        key: 'materials',
        path: 'config/materials',
        label: 'Material',
        description: 'Materialdefinitioner för byggnadskomponenter',
    },
    {
        key: 'operatingCostCategories',
        path: 'config/operatingCostCategories',
        label: 'Driftkostnadskategorier',
        description: 'Kategorier för driftkostnader',
    },
    {
        key: 'conditionAssessment',
        path: 'config/conditionAssessment',
        label: 'Skickbedömning',
        description: 'Kategorier för skickbedömning',
    },
    {
        key: 'locationCategories',
        path: 'locationCategories',
        label: 'Platskategorier',
        description: 'Kategorihierarki för platser i byggnader',
    },
    {
        key: 'enabledBuildingTypes',
        path: 'global/enabledBuildingTypes',
        label: 'Byggnadstyper',
        description: 'Vilka byggnadstyper instansen erbjuder',
    },
    {
        key: 'buildingTypes',
        path: 'global/buildingTypes',
        label: 'Byggnadstyper (äldre iOS)',
        description: 'Byggnadstyper med enabled/createNew-flaggor, läses av iOS-versioner före enabledBuildingTypes',
    },
    {
        key: 'buildingTags',
        path: 'buildingTags',
        label: 'Byggnadstaggar',
        description: 'Taggdefinitioner för byggnader',
    },
];

export interface ConfigExportBundle {
    format: string;
    version: number;
    exportedFrom: {
        projectId: string;
        instanceName?: string;
    };
    exportedAt: string;
    sections: Record<string, Record<string, unknown>>;
}

export interface ImportSectionPreview {
    key: string;
    label: string;
    description: string;
    /** Entry count after sanitization — what an import would write */
    entryCount: number;
    /** Entries currently at the target path; null = path unreadable */
    existingCount: number | null;
    /** Validation errors — a section with errors cannot be imported */
    errors: string[];
    warnings: string[];
}

export interface ImportSectionResult {
    written: number;
    removed: number;
}

interface SanitizeResult {
    data: Record<string, unknown>;
    warnings: string[];
}

const getDefinition = (sectionKey: string): ConfigSectionDefinition | undefined =>
    CONFIG_SECTIONS.find(s => s.key === sectionKey);

/** Superadmin libraries, keyed by section — validated through their own library config. */
const LIBRARY_SECTIONS: Record<string, LibraryConfig<object>> = {
    maintenanceLibraryTemplates: maintenanceLibrary as LibraryConfig<object>,
    lifetimeLibraryTemplates: lifetimeLibrary as LibraryConfig<object>,
    inspectionLibraryTemplates: inspectionLibrary as LibraryConfig<object>,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

/**
 * Strip instance-specific data before it enters the file (and again,
 * defensively, before import): source-instance UIDs, usage tracking, and
 * attachment references pointing at the source instance's Storage bucket.
 */
const sanitizeBuildComponentTypes = (data: Record<string, unknown>): SanitizeResult => {
    const out: Record<string, unknown> = {};
    let strippedAttachments = 0;

    for (const [typeKey, value] of Object.entries(data)) {
        if (!isRecord(value)) {
            out[typeKey] = value;
            continue;
        }
        const type = { ...value };
        delete type.usedByBuildings;
        delete type.createdBy;

        // Legacy nested templates can carry Storage-backed attachments
        if (isRecord(type.maintenance) && isRecord(type.maintenance.templates)) {
            const templates: Record<string, unknown> = {};
            for (const [templateKey, templateValue] of Object.entries(type.maintenance.templates)) {
                if (isRecord(templateValue) && templateValue.instructionalAttachments !== undefined) {
                    const template = { ...templateValue };
                    delete template.instructionalAttachments;
                    templates[templateKey] = template;
                    strippedAttachments++;
                } else {
                    templates[templateKey] = templateValue;
                }
            }
            type.maintenance = { ...type.maintenance, templates };
        }
        out[typeKey] = type;
    }

    const warnings: string[] = [];
    if (strippedAttachments > 0) {
        warnings.push(`Instruktionsbilagor för ${strippedAttachments} inbäddade mallar följer inte med (filerna ligger i källinstansens lagring)`);
    }
    return { data: out, warnings };
};

const sanitizeMaintenanceTemplates = (data: Record<string, unknown>): SanitizeResult => {
    const out: Record<string, unknown> = {};
    let skippedBuildingLocal = 0;
    let strippedAttachments = 0;

    for (const [templateKey, value] of Object.entries(data)) {
        if (!isRecord(value)) {
            out[templateKey] = value;
            continue;
        }
        if (value.buildingKey) {
            skippedBuildingLocal++;
            continue;
        }
        const template = { ...value };
        if (template.instructionalAttachments !== undefined) {
            delete template.instructionalAttachments;
            strippedAttachments++;
        }
        out[templateKey] = template;
    }

    const warnings: string[] = [];
    if (skippedBuildingLocal > 0) {
        warnings.push(`${skippedBuildingLocal} byggnadsspecifika mallar utelämnades`);
    }
    if (strippedAttachments > 0) {
        warnings.push(`Instruktionsbilagor för ${strippedAttachments} mallar följer inte med (filerna ligger i källinstansens lagring)`);
    }
    return { data: out, warnings };
};

/** Library templates carry author UIDs from the source instance; those mean nothing elsewhere. */
const sanitizeLibraryTemplates = (data: Record<string, unknown>): SanitizeResult => {
    const out: Record<string, unknown> = {};
    for (const [templateKey, value] of Object.entries(data)) {
        if (!isRecord(value)) {
            out[templateKey] = value;
            continue;
        }
        const template = { ...value };
        delete template.createdBy;
        delete template.updatedBy;
        out[templateKey] = template;
    }
    return { data: out, warnings: [] };
};

export const sanitizeSectionData = (sectionKey: string, data: Record<string, unknown>): SanitizeResult => {
    if (sectionKey in LIBRARY_SECTIONS) {
        return sanitizeLibraryTemplates(data);
    }
    switch (sectionKey) {
        case 'buildComponentTypes':
            return sanitizeBuildComponentTypes(data);
        case 'maintenanceTemplates':
            return sanitizeMaintenanceTemplates(data);
        default:
            return { data: { ...data }, warnings: [] };
    }
};

const validateLibraryTemplates = (library: LibraryConfig<object>, data: Record<string, unknown>): string[] => {
    const errors: string[] = [];
    for (const [templateKey, value] of Object.entries(data)) {
        if (!isRecord(value)) {
            errors.push(`Mall "${templateKey}": ogiltigt format`);
            continue;
        }
        const sv = isRecord(value.translations) ? value.translations.sv : undefined;
        const name = typeof sv === 'string' ? sv : templateKey;
        if (typeof sv !== 'string' || sv.length === 0) {
            errors.push(`Mall "${name}": saknar namn`);
        }
        if (usesBaseTypes(library) && !Array.isArray(value.baseTypes)) {
            errors.push(`Mall "${name}": saknar bastyper`);
        }
        const fields = library.parseFields(value, templateKey);
        if (!fields) {
            errors.push(`Mall "${name}": saknar obligatoriska fält`);
            continue;
        }
        try {
            library.validateFields(fields, 'full');
        } catch (err) {
            errors.push(`Mall "${name}": ${err instanceof Error ? err.message : 'ogiltiga fält'}`);
        }
    }
    return errors;
};

/** Mirror the constraints the database rules enforce, so a bad file fails before any write. */
export const validateSectionData = (sectionKey: string, data: Record<string, unknown>): string[] => {
    const errors: string[] = [];

    if (sectionKey in LIBRARY_SECTIONS) {
        return validateLibraryTemplates(LIBRARY_SECTIONS[sectionKey], data);
    }

    if (sectionKey === 'buildComponentTypes') {
        for (const [typeKey, value] of Object.entries(data)) {
            if (!isRecord(value)) {
                errors.push(`Komponenttyp "${typeKey}": ogiltigt format`);
                continue;
            }
            if (typeof value.baseType !== 'string') {
                errors.push(`Komponenttyp "${typeKey}": saknar baseType`);
            }
            const translations = value.translations;
            const sv = isRecord(translations) ? translations.sv : undefined;
            if (typeof sv !== 'string' || sv.length < 1 || sv.length > 100) {
                errors.push(`Komponenttyp "${typeKey}": saknar giltig svensk översättning`);
            }
        }
    } else if (sectionKey === 'maintenanceTemplates') {
        for (const [templateKey, value] of Object.entries(data)) {
            if (!isRecord(value)) {
                errors.push(`Mall "${templateKey}": ogiltigt format`);
                continue;
            }
            const name = typeof value.name === 'string' ? value.name : templateKey;
            if (typeof value.name !== 'string') errors.push(`Mall "${name}": saknar namn`);
            if (typeof value.primaryComponentType !== 'string') errors.push(`Mall "${name}": saknar komponenttyp`);
            if (typeof value.interval_type !== 'string') errors.push(`Mall "${name}": saknar intervalltyp`);
            if (typeof value.interval_value !== 'number') errors.push(`Mall "${name}": saknar intervallvärde`);
            if (typeof value.cost !== 'number') errors.push(`Mall "${name}": saknar kostnad`);
            if (typeof value.cost_unit !== 'string') errors.push(`Mall "${name}": saknar kostnadsenhet`);
        }
    } else {
        for (const [entryKey, value] of Object.entries(data)) {
            if (value === null || value === undefined) {
                errors.push(`Post "${entryKey}": saknar värde`);
            }
        }
    }

    return errors;
};

/** Entry counts per section for the current instance; null = missing or unreadable. */
export const getSectionCounts = async (): Promise<Record<string, number | null>> => {
    const db = getDatabase();
    const results = await Promise.allSettled(
        CONFIG_SECTIONS.map(section => get(ref(db, section.path)))
    );

    const counts: Record<string, number | null> = {};
    results.forEach((result, i) => {
        const sectionKey = CONFIG_SECTIONS[i].key;
        if (result.status === 'fulfilled' && result.value.exists() && isRecord(result.value.val())) {
            counts[sectionKey] = Object.keys(result.value.val() as Record<string, unknown>).length;
        } else {
            counts[sectionKey] = null;
        }
    });
    return counts;
};

export const exportConfigSections = async (
    sectionKeys: string[]
): Promise<{ bundle: ConfigExportBundle; warnings: string[] }> => {
    const db = getDatabase();
    const warnings: string[] = [];
    const sections: Record<string, Record<string, unknown>> = {};

    for (const section of CONFIG_SECTIONS.filter(s => sectionKeys.includes(s.key))) {
        const snapshot = await get(ref(db, section.path));
        if (!snapshot.exists() || !isRecord(snapshot.val())) {
            warnings.push(`${section.label}: ingen data att exportera`);
            continue;
        }
        const { data, warnings: sectionWarnings } = sanitizeSectionData(
            section.key,
            snapshot.val() as Record<string, unknown>
        );
        warnings.push(...sectionWarnings.map(w => `${section.label}: ${w}`));
        if (Object.keys(data).length === 0) {
            warnings.push(`${section.label}: inga poster kvar efter filtrering`);
            continue;
        }
        sections[section.key] = data;
    }

    let instanceName: string | undefined;
    try {
        const nameSnapshot = await get(ref(db, 'global/instanceName'));
        if (nameSnapshot.exists() && typeof nameSnapshot.val() === 'string') {
            instanceName = nameSnapshot.val();
        }
    } catch {
        // Metadata only — export works without it
    }

    const bundle: ConfigExportBundle = {
        format: EXPORT_FORMAT,
        version: EXPORT_VERSION,
        exportedFrom: {
            projectId: app.options.projectId ?? '',
            instanceName,
        },
        exportedAt: formatDateForStorage(new Date()),
        sections,
    };
    return { bundle, warnings };
};

export const downloadBundle = (bundle: ConfigExportBundle): void => {
    const source = bundle.exportedFrom.instanceName || bundle.exportedFrom.projectId || 'instans';
    const date = bundle.exportedAt.slice(0, 10);
    const filename = `mf-konfiguration-${generateSlug(source)}-${date}.json`;

    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const parseBundle = (text: string): ConfigExportBundle => {
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        throw new Error('Filen kunde inte läsas som JSON');
    }
    if (!isRecord(parsed) || parsed.format !== EXPORT_FORMAT) {
        throw new Error('Filen är inte en Molnfastigheter-konfigurationsexport');
    }
    if (typeof parsed.version !== 'number' || parsed.version > EXPORT_VERSION) {
        throw new Error('Filen kommer från en nyare version av systemet och kan inte importeras');
    }
    if (!isRecord(parsed.sections)) {
        throw new Error('Filen saknar konfigurationssektioner');
    }
    const knownSections = Object.keys(parsed.sections).filter(key => getDefinition(key));
    if (knownSections.length === 0) {
        throw new Error('Filen innehåller inga kända konfigurationssektioner');
    }
    return parsed as unknown as ConfigExportBundle;
};

export const getImportPreview = async (bundle: ConfigExportBundle): Promise<ImportSectionPreview[]> => {
    const db = getDatabase();
    const previews: ImportSectionPreview[] = [];

    for (const section of CONFIG_SECTIONS) {
        const raw = bundle.sections[section.key];
        if (raw === undefined) continue;

        const errors: string[] = [];
        let warnings: string[] = [];
        let entryCount = 0;

        if (!isRecord(raw)) {
            errors.push('Ogiltigt sektionsformat');
        } else {
            const sanitized = sanitizeSectionData(section.key, raw);
            warnings = sanitized.warnings;
            entryCount = Object.keys(sanitized.data).length;
            errors.push(...validateSectionData(section.key, sanitized.data));
            if (entryCount === 0) {
                errors.push('Sektionen innehåller inga poster');
            }
        }

        let existingCount: number | null = null;
        try {
            const snapshot = await get(ref(db, section.path));
            existingCount = snapshot.exists() && isRecord(snapshot.val())
                ? Object.keys(snapshot.val() as Record<string, unknown>).length
                : 0;
        } catch {
            existingCount = null;
        }

        previews.push({
            key: section.key,
            label: section.label,
            description: section.description,
            entryCount,
            existingCount,
            errors,
            warnings,
        });
    }

    return previews;
};

/**
 * Replace one section: write every entry from the file and remove existing
 * entries not present in it, as a single multi-path update.
 */
export const importConfigSection = async (
    bundle: ConfigExportBundle,
    sectionKey: string
): Promise<ImportSectionResult> => {
    const section = getDefinition(sectionKey);
    const raw = bundle.sections[sectionKey];
    if (!section || !isRecord(raw)) {
        throw new Error('Sektionen finns inte i filen');
    }

    const { data } = sanitizeSectionData(sectionKey, raw);
    const errors = validateSectionData(sectionKey, data);
    if (errors.length > 0) {
        throw new Error(`Ogiltig data: ${errors[0]}`);
    }
    if (Object.keys(data).length === 0) {
        throw new Error('Sektionen innehåller inga poster');
    }

    const db = getDatabase();
    const snapshot = await get(ref(db, section.path));
    const existingKeys = snapshot.exists() && isRecord(snapshot.val())
        ? Object.keys(snapshot.val() as Record<string, unknown>)
        : [];

    const updates: Record<string, unknown> = {};
    for (const [entryKey, value] of Object.entries(data)) {
        updates[`${section.path}/${entryKey}`] = value;
    }
    let removed = 0;
    for (const existingKey of existingKeys) {
        if (!(existingKey in data)) {
            updates[`${section.path}/${existingKey}`] = null;
            removed++;
        }
    }

    await update(ref(db), updates);
    recordActivity('settings.imported', { targetKey: sectionKey });
    return { written: Object.keys(data).length, removed };
};
